import * as React from "react";
import { FieldRow, LabeledSelect } from "./formPrimitives";
import { useAuth } from "../../context/AuthContext";
import { useReferenceData } from "../../context/ReferenceDataContext";
import type { FormState } from "../../utils/formUtils";
import { vorbelegungVerantwortung } from "./verantwortungVorbelegung";
import { MehrfachSelect } from "./widgets/MehrfachSelect";
import { foListeAusForm, foUmschalten } from "../../utils/foUserIds";

/**
 * Verantwortlichkeit (RBAC) — auswählbar, mit Vorbelegung aus dem Nutzer
 * (siehe verantwortungVorbelegung.ts). IDs aus den RBAC-Quellen (users = better-auth
 * user.id = foUserId-Raum; appTeams = AppTeam-UUID = boTeamId-Raum) — NICHT die
 * Legacy-foTeam/boTeam-IDs. Schreibt foUserId/boType/boTeamId in formFields.
 *
 * FO ist seit 28.09.2026 eine MEHRFACHAUSWAHL: `foUserIds` trägt alle FO als
 * kommagetrennte Liste, `foUserId` spiegelt immer den ersten (primären) — so
 * gilt die Pflichtregel „FO oder BO" unverändert mit ≥ 1 FO.
 *
 * Wiederverwendet von Privat- und Firma-Anlage.
 */
const OwnershipSection: React.FC<{
  formFields: FormState;
  updateField: (name: string, value: string) => void;
  defaultFoUserId?: string;
  defaultBoTeamId?: string;
  defaultInhouseContactUserId?: string;
}> = ({ formFields, updateField, defaultFoUserId, defaultBoTeamId, defaultInhouseContactUserId }) => {
  const auth = useAuth();
  const { users, appTeams } = useReferenceData();

  const foIds = foListeAusForm(formFields);
  const boTeamId = typeof formFields.boTeamId === "string" ? formFields.boTeamId : "";
  const inhouseContactUserId =
    typeof formFields.inhouseContactUserId === "string" ? formFields.inhouseContactUserId : "";

  // FO-Liste = User mit FO-Anteil im department ('FO' oder 'FO+BO') und NICHT Admin
  // (ein BO-Sachbearbeiter mit department='FO+BO' darf damit auch FO sein).
  const foUsers = React.useMemo(
    () => users.filter((u) => (u.department ?? "").split("+").includes("FO") && u.role !== "admin"),
    [users],
  );

  // Vorbelegung aus dem angemeldeten Nutzer (verantwortungVorbelegung.ts):
  // FO-Nutzer → selbst FO, Team → BO; ein BO-Nutzer wird NICHT FO. Nur leere
  // Felder — eine Auswahl des Anwenders wird nie überschrieben.
  React.useEffect(() => {
    if (auth.isLoading) return;
    const userId = auth.user?.id ? String(auth.user.id) : "";
    const ich = users.find((u) => u.id === userId);
    const v = vorbelegungVerantwortung({
      userId,
      abteilung: auth.department ?? ich?.department ?? null,
      istAdmin: ich?.role === "admin",
      teamId: defaultBoTeamId || auth.teams?.[0]?.id || "",
    });
    if (!formFields.boType) {
      updateField("boType", v.boType);
      if (v.boTeamId) updateField("boTeamId", v.boTeamId);
    }
    if (!formFields.foUserId) {
      const fo = defaultFoUserId || v.foUserId;
      if (fo) {
        updateField("foUserId", fo);
        updateField("foUserIds", fo);
      }
    }
    if (!formFields.inhouseContactUserId && defaultInhouseContactUserId) {
      updateField("inhouseContactUserId", defaultInhouseContactUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isLoading, auth.user?.id, users.length, defaultFoUserId, defaultBoTeamId, defaultInhouseContactUserId]);

  // Das Kürzel als Zusatz am Zeilenende — sichtbar und durchsuchbar, denn im
  // Haus wird nach „MK“ gesucht, nicht nach dem Namen (T-164, 03.10.2026).
  const userOptions = React.useMemo(
    () => foUsers.map((u) => ({ value: u.id, label: u.name || u.id, hint: u.kuerzel })),
    [foUsers],
  );
  const teamOptions = React.useMemo(
    () => appTeams.map((t) => ({ value: t.id, label: t.name || t.id })),
    [appTeams],
  );
  const inhouseUserOptions = React.useMemo(
    () =>
      [...users]
        .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
        .map((u) => ({ value: u.id, label: u.name || u.id })),
    [users],
  );

  const foNamen = foIds.map((id) => users.find((u) => u.id === id)?.name || id);
  const foBeschriftung =
    foNamen.length === 0 ? "— kein FO —" : foNamen.length <= 2 ? foNamen.join(", ") : `${foNamen.length} ausgewählt`;
  const onFoToggle = (id: string) => {
    const neu = foUmschalten(foIds, id);
    updateField("foUserIds", neu.join(","));
    updateField("foUserId", neu[0] ?? "");
  };
  // Ein bereits gesetzter FO bleibt wählbar (abwählbar), auch wenn er den
  // Abteilungsfilter nicht mehr erfüllt.
  const foOptions = React.useMemo(() => {
    const bekannt = new Set(userOptions.map((o) => o.value));
    const extra = foIds
      .filter((id) => !bekannt.has(id))
      .map((id) => {
        const u = users.find((x) => x.id === id);
        return { value: id, label: u?.name || id, hint: u?.kuerzel };
      });
    return [...userOptions, ...extra];
  }, [userOptions, foIds, users]);

  const onBoChange = (v: string) => {
    if (v) {
      updateField("boType", "TEAM");
      updateField("boTeamId", v);
    } else {
      updateField("boType", "NONE");
      updateField("boTeamId", "");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", rowGap: "14px" }}>
      <FieldRow columns={2}>
        <MehrfachSelect<string>
          label="Frontoffice (FO)"
          name="foUserId"
          value={foIds}
          options={foOptions}
          onToggle={onFoToggle}
          beschriftung={foBeschriftung}
          sucheLabel="Frontoffice durchsuchen"
          fertigKnopf
        />
        <LabeledSelect
          label="Backoffice-Team (BO)"
          name="boTeamId"
          value={boTeamId}
          onChange={onBoChange}
          options={teamOptions}
          placeholder="— kein BO-Team —"
        />
      </FieldRow>
      <FieldRow columns={2}>
        <LabeledSelect
          label="Persönlicher Inhouse Kontakt von"
          name="inhouseContactUserId"
          value={inhouseContactUserId}
          onChange={(v) => updateField("inhouseContactUserId", v)}
          options={inhouseUserOptions}
          placeholder="— keine Auswahl —"
        />
        <div />
      </FieldRow>
    </div>
  );
};

export default OwnershipSection;
